/**
 * Intent headline for the search results page (P25009-445 BR-1).
 *
 * The headline states what the list actually is — "Vinterdäck 235/55R19 till
 * din Volvo XC60" — composed from the active filters, so it can never claim
 * more than the filters guarantee. Parts that aren't active are simply left
 * out and the sentence stays true: "Vinterdäck 235/55R19", "Däck till din
 * Volvo XC60", or nothing at all (the caller falls back to the old heading).
 *
 * The subline follows the same rule. "104 annonser" always holds; the seller
 * count is only claimed when every result is on this page (one page total),
 * because a paginated result set only reveals the sellers of the current
 * page. The mockup's "38 finns inom 20 mil och kan monteras den här veckan"
 * needs distance + mounting data for the FULL result set, which the paginated
 * search cannot provide — deliberately not faked here.
 */

const SEASON_WORDS = { summer: 'Sommar', winter: 'Vinter' };

const CATEGORY_NOUNS = {
  tire: { plain: 'Däck', season: 'däck' },
  rim: { plain: 'Fälgar', season: 'fälgar' },
  wheel: { plain: 'Hjul', season: 'hjul' },
};

/* "winterFriction,winterDubbed" (the Vinter toggle), "summer", possibly with
 * a has_any:/has_all: search-mode prefix. One season word only when every
 * selected value agrees on it. */
const seasonWordFromTiretype = raw => {
  if (!raw) return null;
  const values = String(raw)
    .replace(/^has_(any|all):/, '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
  if (values.length === 0) return null;
  const seasons = values.map(v => (v.toLowerCase().startsWith('winter') ? 'winter' : v === 'summer' ? 'summer' : null));
  const first = seasons[0];
  return first && seasons.every(s => s === first) ? SEASON_WORDS[first] : null;
};

const singleNumber = value => {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return /^\d+(\.\d+)?$/.test(s) ? s : null;
};

/* "235/55R19" from the three dimension params, or `19"` for a rim search
 * that only sets a diameter. Ranges and multi-values give no dimension. */
const dimensionLabel = (params, adType) => {
  const width = singleNumber(params.pub_tireWidthFront);
  const profile = singleNumber(params.pub_profileFront);
  const diameter = singleNumber(params.pub_diameter);
  if (width && profile && diameter) return `${width}/${profile}R${diameter}`;
  if (diameter && adType === 'rim') return `${diameter}"`;
  return null;
};

/**
 * @param {Object} options
 * @param {Object} options.queryParams validated URL query params (pub_* keys)
 * @param {String} [options.carLabel] "Volvo XC60" when the search has a car
 * @returns {String|null} the headline, or null when the filters say nothing
 */
export const buildIntentHeadline = ({ queryParams = {}, carLabel = '' } = {}) => {
  const adType = queryParams.pub_adType || null;
  const noun = CATEGORY_NOUNS[adType] || null;
  const season = seasonWordFromTiretype(queryParams.pub_tiretype);
  const dimension = dimensionLabel(queryParams, adType);
  const car = (carLabel || '').trim();

  /* The season word only makes a compound with a known category —
   * "Vinterdäck", never a bare "Vinter". */
  const subject = noun
    ? season
      ? `${season}${noun.season}`
      : noun.plain
    : season && dimension
      ? /* Season + dimension without a category is still a tyre search in
         * practice (pub_tiretype is a tyre field). */
        `${season}däck`
      : null;

  const head = subject && dimension ? `${subject} ${dimension}` : subject || (dimension ? `Annonser ${dimension}` : null);

  if (!head && !car) return null;
  if (!head) return `Annonser som passar din ${car}`;
  return car ? `${head} till din ${car}` : head;
};

/**
 * Breadcrumb labels matching the headline parts: Däck · Vinter · 235/55R19.
 * @returns {Array<{label: String}>}
 */
export const buildIntentCrumbs = (queryParams = {}) => {
  const adType = queryParams.pub_adType || null;
  const noun = CATEGORY_NOUNS[adType] || null;
  const season = seasonWordFromTiretype(queryParams.pub_tiretype);
  const dimension = dimensionLabel(queryParams, adType);
  return [
    noun ? { label: noun.plain } : null,
    season ? { label: season } : null,
    dimension ? { label: dimension } : null,
  ].filter(Boolean);
};

/**
 * "104 annonser från 23 säljare" — the seller count is only stated when the
 * whole result set is on this page (see file comment).
 *
 * @param {Object} options
 * @param {Number} options.totalItems
 * @param {Array}  options.listings the loaded page of listings
 * @param {Object} [options.pagination]
 * @returns {String|null}
 */
export const buildIntentSubline = ({ totalItems, listings = [], pagination } = {}) => {
  if (!Number.isFinite(totalItems) || totalItems <= 0) return null;
  const countText = totalItems === 1 ? '1 annons' : `${totalItems} annonser`;

  const isCompleteSet =
    !!pagination && (pagination.totalPages === 1 || pagination.paginationUnsupported === true);
  if (!isCompleteSet) return countText;

  const sellerCount = new Set(
    listings.map(l => l?.author?.id?.uuid).filter(Boolean)
  ).size;
  if (sellerCount < 2) return countText;
  return `${countText} från ${sellerCount} säljare`;
};
